// @ts-nocheck
import { useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { trackAffiliateClick, storeReferralInSession } from '@/utils/affiliateTracking';

const recordDetailedClick = async (data: any) => {
  try {
    await supabase.from('affiliate_click_tracking').insert({
      ...data,
      visitor_ip: null, // Will be populated by server if needed
      user_agent: navigator.userAgent,
      session_id: sessionStorage.getItem('session_id') || crypto.randomUUID()
    });
  } catch (error) {
    console.error('Error recording detailed click:', error);
  }
};

const AffiliateTracker = () => {
  useEffect(() => {
    const run = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const codeParam = params.get('ref') || params.get('code');
        if (!codeParam) return;

        const code = codeParam.toUpperCase();
        console.log('🔍 [AffiliateTracker] Processing referral code:', code);
        
        // Try to find referral link first
        const { data: link, error } = await supabase
          .from('affiliate_referral_links')
          .select('code, affiliate_id, id')
          .ilike('code', code)
          .maybeSingle();

        if (error) {
          console.warn('[AffiliateTracker] Error validating referral link:', error.message);
        }

        // If no link found, try to find affiliate by referral_code directly
        let affiliateId = link?.affiliate_id;
        let linkId = link?.id;
        
        if (!affiliateId) {
          console.log('🔍 [AffiliateTracker] No referral link found, checking affiliates.referral_code...');
          const { data: affiliate, error: affiliateError } = await supabase
            .from('affiliates')
            .select('id, referral_code')
            .ilike('referral_code', code)
            .eq('status', 'active')
            .maybeSingle();
          
          if (affiliateError) {
            console.warn('[AffiliateTracker] Error checking affiliates:', affiliateError.message);
          }
          
          if (affiliate) {
            console.log('✅ [AffiliateTracker] Found affiliate by referral_code:', affiliate.id);
            affiliateId = affiliate.id;
            // Create a referral link record if it doesn't exist
            const { data: newLink } = await supabase
              .from('affiliate_referral_links')
              .insert({
                affiliate_id: affiliate.id,
                code: affiliate.referral_code,
                name: 'Auto-generated from referral_code',
                url: `https://recruitlook.com/?ref=${affiliate.referral_code}`,
                is_active: true
              })
              .select('id')
              .single();
            linkId = newLink?.id;
          }
        }

        if (!affiliateId) {
          console.warn('[AffiliateTracker] No valid affiliate found for code:', code);
          return;
        }

        console.log('✅ [AffiliateTracker] Valid affiliate found:', affiliateId);

        // Persist for lifetime attribution
        localStorage.setItem('affiliateReferralCode', code);
        localStorage.setItem('affiliateId', affiliateId);
        console.log('💾 [AffiliateTracker] Stored in localStorage:', { code, affiliateId });
        
        // Also keep session storage for flows already implemented
        storeReferralInSession(code, document.referrer || window.location.href);

        // Record click via Edge Function with UTM parameters
        const urlParams = new URLSearchParams(window.location.search);
        const utmData = {
          utm_source: urlParams.get('utm_source'),
          utm_medium: urlParams.get('utm_medium'),
          utm_campaign: urlParams.get('utm_campaign'),
          utm_content: urlParams.get('utm_content'),
          utm_term: urlParams.get('utm_term')
        };
        
        console.log('📊 [AffiliateTracker] Recording click...');
        await trackAffiliateClick(code, { 
          source_url: window.location.href,
          ...utmData
        });
        
        // Also record detailed tracking if we have a link_id
        if (linkId) {
          await recordDetailedClick({
            referral_link_id: linkId,
            affiliate_id: affiliateId,
            landing_url: window.location.href,
            referrer_url: document.referrer,
            ...utmData
          });
        }
        
        console.log('✅ [AffiliateTracker] Click tracking complete');
        // Optional: clean URL without params
        const url = new URL(window.location.href);
        url.searchParams.delete('ref');
        url.searchParams.delete('code');
        window.history.replaceState({}, document.title, url.pathname + (url.search ? '?' + url.search : ''));
      } catch (e) {
        console.error('[AffiliateTracker] Unexpected error:', e);
      }
    };
    run();
  }, []);

  return null;
};

export default AffiliateTracker;
